import React, { useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { ChevronLeft, ChevronRight, Check, Bath, Package, X, Maximize2 } from "lucide-react";
import { Modal } from "@mantine/core";
import moment from "moment";

interface RoomCardProps {
  room: PropertyToEdit["rooms"][number];
  startDate?: string;
  endDate?: string;
  onSelectRoom?: (room: PropertyToEdit["rooms"][number]) => void;
  propertyData?: PropertyToEdit;
  isSelected?: boolean;
}

const RoomCard: React.FC<RoomCardProps> = ({
  room,
  startDate,
  endDate,
  onSelectRoom,
  propertyData,
  isSelected = false,
}) => {
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [showAmenitiesModal, setShowAmenitiesModal] = useState(false);
  const [showGallery, setShowGallery] = useState(false); // ✅ NEW: Gallery modal state
  const [galleryIndex, setGalleryIndex] = useState(0); // ✅ NEW: Track current image in gallery

  // ✅ Determine display mode from propertyData
  const isSharedProperty = propertyData?.is_shared_property ?? true;
  const isIncludedMode = !isSharedProperty; // If not shared, all rooms are included

  // 🐛 DEBUG LOGGING - Monitor property values
  React.useEffect(() => {
    console.group(`🏠 RoomCard Debug - ${room.name}`);
    console.log("📦 propertyData exists:", !!propertyData);
    console.log(
      "🔑 is_shared_property raw value:",
      propertyData?.is_shared_property
    );
    console.log(
      "🔍 Type of is_shared_property:",
      typeof propertyData?.is_shared_property
    );
    console.log("✅ isSharedProperty (computed):", isSharedProperty);
    console.log("📋 isIncludedMode (computed):", isIncludedMode);
    console.log("🎯 isSelected:", isSelected);
    console.log("🚪 Room info:", {
      id: room.id,
      name: room.name,
      to_book: room.to_book,
      availability_start: room.availability_start,
      availability_end: room.availability_end,
    });
    console.log("💰 Price:", room.price_per_month);
    console.log("📅 Search dates:", { startDate, endDate });

    // Additional helpful debug info
    if (propertyData) {
      console.log("🏢 Property title:", propertyData.title);
      console.log("🔢 Total rooms:", propertyData.rooms?.length);
    }
    console.groupEnd();
  }, [
    propertyData,
    isSharedProperty,
    isIncludedMode,
    isSelected,
    room,
    startDate,
    endDate,
  ]);

  const images =
    room.room_images?.length && room.room_images.length > 0
      ? room.room_images
      : [
          {
            id: 0,
            url: "https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg?auto=compress&cs=tinysrgb&w=600",
          },
        ];

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  // ✅ NEW: Gallery navigation
  const nextGalleryImage = () => {
    setGalleryIndex((prev) => (prev + 1) % images.length);
  };

  const prevGalleryImage = () => {
    setGalleryIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // ✅ NEW: Open gallery at current image
  const openGallery = (e: React.MouseEvent) => {
    e.stopPropagation();
    setGalleryIndex(currentImage);
    setShowGallery(true);
  };

  // ✅ NEW: Keyboard navigation for gallery
  React.useEffect(() => {
    if (!showGallery) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevGalleryImage();
      if (e.key === "ArrowRight") nextGalleryImage();
      if (e.key === "Escape") setShowGallery(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showGallery, images.length]);

  // ✅ Check if room has any valid availability windows (30+ day gaps) - MOVED UP
  const hasValidAvailability = () => {
    if (!isSharedProperty) return true; // Entire properties don't need this check

    const bookings = (room.booked_dates || []) as [string, string][];

    const actualStart = room.next_available_date
      ? moment(room.next_available_date)
      : moment(room.availability_start);

    const roomEnd = moment(room.availability_end);

    if (!roomEnd.isValid()) return false;

    // No bookings - check total period
    if (bookings.length === 0) {
      return roomEnd.diff(actualStart, "days") >= 30;
    }

    const sortedBookings = [...bookings].sort((a, b) =>
      moment(a[0]).diff(moment(b[0]))
    );

    // Check gap before first booking
    const firstGap = moment(sortedBookings[0][0]).diff(actualStart, "days");
    if (firstGap >= 30) return true;

    // Check gaps between bookings
    for (let i = 0; i < sortedBookings.length - 1; i++) {
      const gap =
        moment(sortedBookings[i + 1][0]).diff(
          moment(sortedBookings[i][1]),
          "days"
        ) - 1;
      if (gap >= 30) return true;
    }

    // Check gap after last booking
    const lastGap =
      roomEnd.diff(
        moment(sortedBookings[sortedBookings.length - 1][1]),
        "days"
      ) - 1;
    if (lastGap >= 30) return true;

    return false;
  };

  const roomHasAvailability = hasValidAvailability();

  // ✅ NEW: Check if room's availability starts AFTER the search end date
  const isAvailabilityInFuture = () => {
    if (!endDate || !room.availability_start || !startDate) return false;

    const searchEnd = moment(endDate);
    const roomStart = moment(room.availability_start);
    const searchStart = moment(startDate);

    // Room starts after the search period ends
    // AND room doesn't overlap with search period at all
    return roomStart.isAfter(searchEnd, "day");
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(undefined, {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getAvailabilityText = (
    room: any,
    startDate?: string,
    endDate?: string
  ) => {
    // ✅ For shared properties, show room availability
    if (isSharedProperty) {
      // ✅ Check if room has NO valid availability at all (must be checked early)
      if (!roomHasAvailability) {
        return "No Availability";
      }

      // ✅ Check if availability is in the future
      if (isAvailabilityInFuture() && room.availability_start) {
        return `Available from ${formatDate(room.availability_start)}`;
      }

      // If dates are selected and room is bookable
      if (startDate && endDate) {
        if (room.to_book) return "Available";
        return room.next_available_date
          ? `Available from ${formatDate(room.next_available_date)}`
          : "Not Available";
      }

      // No dates selected - show next available or status
      if (room.next_available_date) {
        const nextDate = new Date(room.next_available_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // If available today or in the past
        if (nextDate <= today) {
          return "Available Now";
        }
        return `Available from ${formatDate(room.next_available_date)}`;
      }

      return "Not Available";
    }

    // ✅ For entire properties, this shouldn't show but kept for safety
    return "Available";
  };

  // Get bathroom type display info
  const getBathroomInfo = (bathroomType?: string) => {
    switch (bathroomType) {
      case "ensuite_bathroom":
        return {
          label: "Ensuite Bathroom",
          color:
            "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700",
          icon: "✓",
          isPrivate: true,
        };
      case "external_private_bathroom":
        return {
          label: "Private Bathroom",
          color:
            "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700",
          icon: "✓",
          isPrivate: true,
        };
      case "shared_bathroom":
        return {
          label: "Shared Bathroom",
          color:
            "bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-neutral-600",
          icon: "",
          isPrivate: false,
        };
      default:
        return null;
    }
  };

  const bathroomInfo = getBathroomInfo(room.bathroom_type);

  const availabilityInFuture = isAvailabilityInFuture();

  const description =
    room.description && room.description.length > 160
      ? showFullDesc
        ? room.description
        : room.description.slice(0, 160) + "..."
      : room.description || "No description available.";

  const handleSelectRoom = () => {
    // ✅ Only allow selection if in selectable mode
    if (onSelectRoom && !isIncludedMode) {
      onSelectRoom(room);
    }
  };

  const totalAmenities = room.amenities?.length || 0;
  const hasMoreAmenities = totalAmenities > 5;

  // ✅ NEW: Determine badge color based on availability state
  const getAvailabilityBadgeColor = () => {
    if (availabilityInFuture) {
      return "bg-orange-500"; // Orange for future availability
    }
    if (!roomHasAvailability) {
      return "bg-red-500"; // Red for no availability
    }
    return room.to_book ? "bg-green-500" : "bg-orange-500"; // Green for available, orange for limited
  };

  return (
    <>
      <div
        className={`flex flex-col md:flex-row bg-white dark:bg-neutral-800 rounded-2xl border-2 shadow-sm hover:shadow-lg transition-all overflow-hidden ${
          isIncludedMode
            ? "border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-900/10" // ✅ Different style for included rooms
            : isSelected
            ? "border-[#f5694b] shadow-lg ring-2 ring-[#f5694b]/20"
            : "border-gray-200 dark:border-neutral-700 hover:border-gray-300 dark:hover:border-neutral-600"
        }`}
      >
        {/* Left - Image Carousel */}
        <div className="relative md:w-[300px] w-full md:h-[300px] h-[220px] overflow-hidden flex-shrink-0 group">
          <img
            src={images[currentImage].url}
            alt={room.name}
            className="w-full h-full object-cover object-center transition-all duration-500 ease-in-out cursor-pointer"
            onClick={openGallery} // ✅ NEW: Click to open gallery
          />

          {/* ✅ NEW: Expand icon overlay */}
          <button
            onClick={openGallery}
            className="absolute top-3 right-3 bg-white/90 hover:bg-white dark:bg-neutral-800/90 dark:hover:bg-neutral-800 text-gray-800 dark:text-white rounded-lg p-2 shadow-md transition-all opacity-0 group-hover:opacity-100 z-10"
            aria-label="View gallery"
          >
            <Maximize2 size={16} />
          </button>

          {/* Selected Overlay - Only for selectable mode */}
          {isSelected && !isIncludedMode && (
            <div className="absolute inset-0 bg-[#f5694b]/20 flex items-center justify-center backdrop-blur-[1px]">
              <div className="bg-white dark:bg-neutral-800 rounded-full p-3 shadow-lg">
                <Check size={32} className="text-[#f5694b]" strokeWidth={3} />
              </div>
            </div>
          )}

          {/* Carousel Controls */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/90 hover:bg-white dark:bg-neutral-800/90 dark:hover:bg-neutral-800 text-gray-800 dark:text-white rounded-full p-2 shadow-md transition-all hover:scale-110 z-10"
                aria-label="Previous image"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/90 hover:bg-white dark:bg-neutral-800/90 dark:hover:bg-neutral-800 text-gray-800 dark:text-white rounded-full p-2 shadow-md transition-all hover:scale-110 z-10"
                aria-label="Next image"
              >
                <ChevronRight size={18} />
              </button>

              {/* Image indicators */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImage(i);
                    }}
                    className={`h-2 w-2 rounded-full transition-all ${
                      i === currentImage
                        ? "bg-white w-4"
                        : "bg-white/60 hover:bg-white/80"
                    }`}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Status Badge - Different for each mode */}
          {isIncludedMode ? (
            // ✅ "Included in Booking" badge for entire property
            <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-blue-500 text-white text-xs px-3 py-1.5 font-semibold shadow-lg z-10">
              <Package size={14} strokeWidth={2.5} />
              Included in Booking
            </div>
          ) : (
            // ✅ Availability badge with dynamic color
            <span
              className={`absolute top-3 left-3 rounded-full text-white text-xs px-3 py-1 font-semibold shadow-lg z-10 ${getAvailabilityBadgeColor()}`}
            >
              {getAvailabilityText(room, startDate, endDate)}
            </span>
          )}
        </div>

        {/* Right - Info */}
        <div className="flex-1 p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {room.name}
              </h3>
              {/* ✅ Selected badge only for selectable mode */}
              {isSelected && !isIncludedMode && (
                <span className="hidden md:flex items-center gap-1 px-2 py-1 bg-[#f5694b]/10 text-[#f5694b] text-xs font-semibold rounded-full flex-shrink-0">
                  <Check size={12} strokeWidth={3} />
                  Selected
                </span>
              )}
            </div>

            {/* Bathroom Type Badge */}
            {bathroomInfo && (
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 mb-3 ${bathroomInfo.color}`}
              >
                <Bath size={16} strokeWidth={2.5} />
                <span className="text-sm font-semibold">
                  {bathroomInfo.icon && (
                    <span className="mr-1">{bathroomInfo.icon}</span>
                  )}
                  {bathroomInfo.label}
                </span>
              </div>
            )}

            {/* Description with Read More */}
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">
              {description}
            </p>
            {room.description && room.description.length > 160 && (
              <button
                onClick={() => setShowFullDesc(!showFullDesc)}
                className="text-xs text-[#f5694b] hover:text-[#e55a3c] dark:text-orange-400 dark:hover:text-orange-300 hover:underline font-medium mb-3"
              >
                {showFullDesc ? "Read less" : "Read more"}
              </button>
            )}

            {/* Room Details Grid */}
            <ul className="text-xs text-gray-700 dark:text-gray-300 grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3 mt-3">
              {room.size && (
                <li className="flex items-center gap-1">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Size:
                  </span>
                  <span>{room.size} m²</span>
                </li>
              )}
              {room.max_guests && (
                <li className="flex items-center gap-1">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Guests:
                  </span>
                  <span>{room.max_guests}</span>
                </li>
              )}
              {room.bed_type && (
                <li className="flex items-center gap-1">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Bed:
                  </span>
                  <span>{room.bed_type.name}</span>
                </li>
              )}
              {room.room_type && (
                <li className="flex items-center gap-1">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Type:
                  </span>
                  <span>{room.room_type.name}</span>
                </li>
              )}
            </ul>

            {/* Amenities Tags */}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {room.amenities?.slice(0, 5).map((a) => (
                <span
                  key={a.id}
                  className="bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 text-xs px-2.5 py-1 rounded-full font-medium"
                >
                  {a.name}
                </span>
              ))}
              {hasMoreAmenities && (
                <button
                  onClick={() => setShowAmenitiesModal(true)}
                  className="bg-[#f5694b] hover:bg-[#e55a3c] text-white text-xs px-2.5 py-1 rounded-full font-semibold transition-colors"
                >
                  +{totalAmenities - 5} more
                </button>
              )}
            </div>
          </div>

          {/* Price and Action */}
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t border-gray-200 dark:border-neutral-700 pt-4">
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                ${room.price_per_month?.toLocaleString()}
                <span className="text-sm text-gray-500 dark:text-gray-400 font-normal ml-1">
                  / month
                </span>
              </p>
            </div>

            {/* ✅ NEW: Button logic with "availability in future" state */}
            {isIncludedMode ? (
              // ✅ For Entire Property: Show "Included" badge instead of button
              <div className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-full">
                <Package
                  size={16}
                  className="text-blue-600 dark:text-blue-400"
                  strokeWidth={2.5}
                />
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                  Included
                </span>
              </div>
            ) : availabilityInFuture ? // ✅ NEW: Room availability starts in the future - HIDE BUTTON COMPLETELY
            null : !roomHasAvailability ? (
              // ✅ Room has no valid 30+ day windows
              <button
                type="button"
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-full border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-6 py-2.5 text-sm font-semibold text-red-600 dark:text-red-400 cursor-not-allowed"
                disabled
                title="No 30+ day availability windows"
              >
                No Availability
              </button>
            ) : room.to_book ? (
              // ✅ For Shared Property: Show selectable button
              <button
                type="button"
                onClick={handleSelectRoom}
                className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border-2 px-6 py-2.5 text-sm font-semibold shadow-sm transition-all ${
                  isSelected
                    ? "bg-[#f5694b] border-[#f5694b] text-white hover:bg-[#e55a3c] hover:border-[#e55a3c]"
                    : "border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-600 hover:border-gray-400 dark:hover:border-neutral-500"
                }`}
              >
                {isSelected ? (
                  <>
                    <Check size={16} strokeWidth={3} />
                    Selected
                  </>
                ) : (
                  "Select Room"
                )}
              </button>
            ) : (
              // ✅ Not available state
              <button
                type="button"
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-full border-2 border-gray-200 dark:border-neutral-700 bg-gray-100 dark:bg-neutral-800 px-6 py-2.5 text-sm font-semibold text-gray-400 dark:text-gray-500 cursor-not-allowed"
                disabled
              >
                Not Available
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ✅ NEW: Full-Screen Gallery Modal */}
    {/* ✅ IMPROVED: Full-Screen Gallery Modal with Multiple Exit Options */}
{showGallery && (
  <div 
    className="fixed inset-0 z-[9999] bg-black"
    onClick={() => setShowGallery(false)} // ✅ Click anywhere on background to close
  >
    {/* Header */}
    <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
      <div className="text-white">
        <h3 className="text-lg font-semibold">{room.name}</h3>
        <p className="text-sm text-white/80">
          {galleryIndex + 1} / {images.length}
        </p>
      </div>
      
      {/* ✅ IMPROVED: Larger, more visible close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowGallery(false);
        }}
        className="bg-white hover:bg-gray-100 text-gray-900 rounded-full p-3 shadow-xl transition-all hover:scale-110 flex items-center gap-2"
        aria-label="Close gallery"
      >
        <X size={24} />
        <span className="text-sm font-semibold hidden sm:inline">Close</span>
      </button>
    </div>

    {/* ✅ NEW: ESC key hint */}
    <div className="absolute top-20 left-1/2 -translate-x-1/2 z-10 bg-black/60 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full animate-fade-in">
      Press <kbd className="px-2 py-1 bg-white/20 rounded mx-1">ESC</kbd> or click outside to close
    </div>

    {/* Main Image - Click to close */}
    <div 
      className="absolute inset-0 flex items-center justify-center p-4"
      onClick={() => setShowGallery(false)} // ✅ Click on background area to close
    >
      <img
        src={images[galleryIndex].url}
        alt={`${room.name} - Image ${galleryIndex + 1}`}
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()} // ✅ Don't close when clicking image itself
      />
    </div>

    {/* Navigation Arrows */}
    {images.length > 1 && (
      <>
        <button
          onClick={(e) => {
            e.stopPropagation();
            prevGalleryImage();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-gray-900 rounded-full p-4 shadow-xl transition-all hover:scale-110"
          aria-label="Previous image"
        >
          <ChevronLeft size={32} strokeWidth={2.5} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            nextGalleryImage();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-gray-900 rounded-full p-4 shadow-xl transition-all hover:scale-110"
          aria-label="Next image"
        >
          <ChevronRight size={32} strokeWidth={2.5} />
        </button>
      </>
    )}

    {/* Thumbnail Strip */}
    <div 
      className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent"
      onClick={(e) => e.stopPropagation()} // ✅ Don't close when interacting with thumbnails
    >
      <div className="flex gap-2 overflow-x-auto scrollbar-hide justify-center pb-2">
        {images.map((img, i) => (
          <button
            key={img.id}
            onClick={(e) => {
              e.stopPropagation();
              setGalleryIndex(i);
            }}
            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all ${
              i === galleryIndex
                ? "ring-4 ring-white scale-110"
                : "opacity-60 hover:opacity-100"
            }`}
          >
            <img
              src={img.url}
              alt={`Thumbnail ${i + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  </div>
)}

      {/* Amenities Modal */}
      <Modal
        opened={showAmenitiesModal}
        onClose={() => setShowAmenitiesModal(false)}
        size="lg"
        centered
        title={
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold text-gray-900 dark:text-white">
              All Amenities & Features
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({totalAmenities} total)
            </span>
          </div>
        }
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        styles={{
          header: {
            paddingBottom: "1rem",
            borderBottom: "1px solid",
            borderColor: "var(--mantine-color-gray-2)",
          },
          body: {
            paddingTop: "1.5rem",
          },
        }}
      >
        <div className="space-y-4">
          {/* Room Name */}
          <div className="p-3 bg-gray-50 dark:bg-neutral-800 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Room:</p>
            <p className="text-base font-semibold text-gray-900 dark:text-white">
              {room.name}
            </p>
          </div>

          {/* Display Mode Badge */}
          {isIncludedMode && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-l-blue-500">
              <div className="flex items-center gap-2">
                <Package
                  size={16}
                  className="text-blue-600 dark:text-blue-400"
                  strokeWidth={2.5}
                />
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                  This room is included in your entire property booking
                </span>
              </div>
            </div>
          )}

          {/* Future Availability Notice */}
          {availabilityInFuture && room.availability_start && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-l-4 border-l-yellow-500">
              <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-300">
                This room becomes available from{" "}
                {formatDate(room.availability_start)}
              </p>
            </div>
          )}

          {/* Bathroom Type in Modal */}
          {bathroomInfo && (
            <div className="p-3 bg-gray-50 dark:bg-neutral-800 rounded-lg border-l-4 border-l-[#f5694b]">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Bathroom:
              </p>
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 ${bathroomInfo.color}`}
              >
                <Bath size={16} strokeWidth={2.5} />
                <span className="text-sm font-semibold">
                  {bathroomInfo.icon && (
                    <span className="mr-1">{bathroomInfo.icon}</span>
                  )}
                  {bathroomInfo.label}
                </span>
              </div>
            </div>
          )}

          {/* Amenities Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {room.amenities?.map((amenity) => (
              <div
                key={amenity.id}
                className="flex items-center gap-2 p-3 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg hover:border-[#f5694b] dark:hover:border-[#f5694b] transition-colors"
              >
                <div className="w-2 h-2 bg-[#f5694b] rounded-full flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  {amenity.name}
                </span>
              </div>
            ))}
          </div>

          {/* Close Button */}
          <div className="pt-4 border-t border-gray-200 dark:border-neutral-700">
            <button
              onClick={() => setShowAmenitiesModal(false)}
              className="w-full px-4 py-2.5 bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RoomCard;